import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import usePageMeta from "@/lib/usePageMeta";
import { motion } from "framer-motion";
import { MapPin, Phone, Heart, ShieldCheck, Zap, ArrowRight } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6 },
};

export default function APropos() {
  const { t } = useTranslation();
  usePageMeta("about");

  return (
    <div className="min-h-screen bg-bg text-[var(--text)]">
      <PublicHeader />

      <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-8 sm:pt-14 pb-10 text-center">
        <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-secondary-c bg-secondary-c/10 px-3 py-1 rounded-full">
          <Heart className="w-3.5 h-3.5" /> {t("about.badge")}
        </motion.span>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="font-display text-4xl sm:text-5xl md:text-6xl mt-4 leading-tight">
          {t("about.title_1")} <span className="font-serif-italic text-primary-c">{t("about.title_accent")}</span>
        </motion.h1>
        <p className="mt-4 text-text-secondary text-base sm:text-lg max-w-2xl mx-auto">{t("about.subtitle")}</p>
      </section>

      {/* Founder */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <motion.div {...fadeUp} className="grid md:grid-cols-[180px_1fr] gap-6 sm:gap-8 items-center card-c">
          <div className="mx-auto md:mx-0">
            <div className="w-32 h-32 sm:w-40 sm:h-40 md:w-44 md:h-44 rounded-3xl bg-gradient-to-br from-primary-c to-[#1F5E47] flex items-center justify-center text-white shadow-xl">
              <span className="font-display text-5xl sm:text-6xl">L</span>
            </div>
          </div>
          <div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-secondary-c">{t("founder.label")}</span>
            <h3 className="font-display text-2xl sm:text-3xl mt-2 leading-tight">
              {t("founder.quote_1")} <span className="font-serif-italic text-primary-c">{t("founder.quote_accent")}</span> {t("founder.quote_2")}
            </h3>
            <p className="mt-4 text-sm sm:text-base text-text-secondary leading-relaxed">{t("founder.text")}</p>
            <div className="mt-5 flex flex-wrap gap-3 text-xs sm:text-sm">
              <span className="inline-flex items-center gap-1.5 text-text-secondary"><MapPin className="w-4 h-4 text-primary-c" /> {t("founder.based")}</span>
              <a href="mailto:contact@oresto.ch" className="inline-flex items-center gap-1.5 text-primary-c font-semibold">
                <Phone className="w-4 h-4" /> contact@oresto.ch
              </a>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Values */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <motion.h2 {...fadeUp} className="font-display text-3xl sm:text-4xl text-center leading-tight">
          {t("about.values_title")}
        </motion.h2>
        <div className="grid md:grid-cols-3 gap-4 mt-10">
          {[
            { icon: ShieldCheck, k: "v1" },
            { icon: Heart, k: "v2" },
            { icon: Zap, k: "v3" },
          ].map(({ icon: Icon, k }, i) => (
            <motion.div key={i} {...fadeUp} className="card-c">
              <div className="w-10 h-10 rounded-2xl bg-primary-c/10 flex items-center justify-center">
                <Icon className="w-5 h-5 text-primary-c" strokeWidth={1.7} />
              </div>
              <h3 className="mt-3 font-display text-xl">{t(`about.${k}_title`)}</h3>
              <p className="text-sm text-text-secondary mt-2 leading-relaxed">{t(`about.${k}_desc`)}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Timeline */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <motion.h2 {...fadeUp} className="font-display text-3xl sm:text-4xl text-center leading-tight">
          {t("about.timeline_title")}
        </motion.h2>
        <div className="mt-10 space-y-6">
          {["2025_q4", "2026_q1", "2026_q2", "2026_q3"].map((k, i) => (
            <motion.div key={k} {...fadeUp} className="flex gap-4">
              <div className="shrink-0 w-14">
                <div className="w-10 h-10 rounded-2xl bg-primary-c text-white font-display text-sm flex items-center justify-center">{i + 1}</div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-secondary-c font-semibold">{t(`about.tl_${k}_date`)}</p>
                <h4 className="font-display text-lg mt-0.5">{t(`about.tl_${k}_title`)}</h4>
                <p className="text-sm text-text-secondary mt-1">{t(`about.tl_${k}_desc`)}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-14">
        <motion.div {...fadeUp} className="rounded-3xl p-8 sm:p-10 text-center bg-gradient-to-br from-[#F5EFDE] to-[#E8F1EA]">
          <h2 className="font-display text-2xl sm:text-3xl leading-tight">{t("about.cta_title")}</h2>
          <p className="mt-3 text-text-secondary max-w-xl mx-auto text-sm sm:text-base">{t("about.cta_desc")}</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" className="btn-primary inline-flex items-center justify-center gap-2 group">
              {t("hero.cta_primary")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/reserver" className="btn-secondary inline-flex items-center justify-center gap-2">
              {t("book.cta")}
            </Link>
          </div>
        </motion.div>
      </section>

      <PublicFooter />
    </div>
  );
}
